import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { getClientId } from "../utils/utils";
import { clientService } from "../services/clientService";

const Breadcrumb = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [clientDetail, setClientDetail] = useState({ name: "", logo: "" });

  const client_id = getClientId();

  const getClient = async () => {
    if (client_id) {
      const response = await clientService.getClientById(client_id);
      if (response.success) {
        const { client_name, logo_url } = response.data;
        setClientDetail({ name: client_name, logo: logo_url });
      }
    } else setClientDetail({});
  };
  useEffect(() => {
    getClient();
  }, [client_id, location]);
  // Parse the current path and search params
  const pathSegments = location.pathname.split("/").filter(Boolean);
  const searchParams = new URLSearchParams(location.search);

  // Route name mappings
  const routeNameMap = {
    projects: "Projects",
    "test-cases": "Test Cases",
    "test-runs": "Test Runs",
    "test-plans": "Test Plans",
    settings: "Settings",
    clients: "Clients",
    users: "Users",
    reports: "Reports",
    dashboard: "Dashboard",
    "my-account": "My Profile",
    documentation: "Documentation",
    support: "Support",
    baselines: "Baselines",
    suites: "Suites",
  };

  // Settings section mappings
  const settingsSectionMap = {
    details: "Client Details",
    users: "Users",
    roles: "Roles",
    projects: "Projects & Memberships",
    audit: "Audit Logs",
    webhook: "Webhook",
    api_key:"API Key",
    security: "Security & SSO",
    sso_configuration: "SSO Configuration",
    integrations: "Integrations",
    network: "Network / IP",
    api: "API & Automation",
    alerts: "Alerts",
    notifications: "Notifications",
    templates: "Test Case / Test Run Layout",
    fields: "Fields",
  };

  // Build breadcrumb items
  const buildBreadcrumbs = () => {
    // All Clients → Edit Client (chief-admin edit flow; no "Settings" root crumb)
    if (
      pathSegments[0] === "settings" &&
      searchParams.get("from") === "all-clients"
    ) {
      return [
        { label: "All Clients", path: "/clients", isActive: false },
        {
          label: "Edit Client",
          path: `${location.pathname}${location.search}`,
          isActive: true,
        },
      ];
    }

    if (
      pathSegments[0] === "client-configuration" &&
      searchParams.get("from") === "all-clients"
    ) {
      return [
        { label: "All Clients", path: "/clients", isActive: false },
        {
          label: "System Configuration",
          path: `${location.pathname}${location.search}`,
          isActive: true,
        },
      ];
    }

    if (
      pathSegments[0] === "projects" &&
      searchParams.get("from") === "all-clients"
    ) {
      return [
        { label: "All Clients", path: "/clients", isActive: false },
        {
          label: "Projects",
          path: `${location.pathname}${location.search}`,
          isActive: true,
        },
      ];
    }

    const breadcrumbs = [];

    // Add home/root
    if (pathSegments.length > 0) {
      const firstSegment = pathSegments[0];
      breadcrumbs.push({
        label:
          routeNameMap[firstSegment] ||
          firstSegment.charAt(0).toUpperCase() + firstSegment.slice(1),
        path: `/${firstSegment}`,
        isActive: pathSegments.length === 1 && !searchParams.get("section"),
      });
    }

    // Handle settings with section parameter
    if (pathSegments[0] === "settings" && searchParams.get("section")) {
      const section = searchParams.get("section");
      breadcrumbs.push({
        label: settingsSectionMap[section] || section,
        path: `/settings?section=${section}`,
        isActive: true,
      });
    }

    // Handle nested routes (e.g., /projects/123/test-cases)
    if (pathSegments.length > 1) {
      for (let i = 1; i < pathSegments.length; i++) {
        const segment = pathSegments[i];

        // Skip numeric IDs in breadcrumb display
        if (!isNaN(segment)) {
          continue;
        }

        const path = "/" + pathSegments.slice(0, i + 1).join("/");
        breadcrumbs.push({
          label:
            routeNameMap[segment] ||
            segment.charAt(0).toUpperCase() +
              segment.slice(1).replace(/-/g, " "),
          path: path,
          isActive: i === pathSegments.length - 1,
        });
      }
    }

    return breadcrumbs;
  };

  const breadcrumbs = buildBreadcrumbs();

  // Don't show breadcrumb if we're at root or no breadcrumbs
  if (breadcrumbs.length === 0) {
    return null;
  }

  const isFromAllClients = searchParams.get("from") === "all-clients";

  return (
    <nav className="flex items-center space-x-2">
      {/* Logo/Icon — hide when opened from All Clients so the bar doesn't imply an active client context */}
      {clientDetail.logo && !isFromAllClients && (
        <div className="flex items-center">
          <img
            src={clientDetail.logo}
            style={{
              height: "30px",
              maxWidth: "80px",
              objectFit: "contain",
            }}
            alt=""
          />
        </div>
      )}

      {/* Breadcrumb items */}
      {breadcrumbs.map((crumb, index) => (
        <React.Fragment key={`${crumb.path}-${crumb.label}-${index}`}>
          {index ? <ChevronRight className="w-4 h-4 text-gray-400" /> : null}
          {crumb.isActive ? (
            <span className="text-gray-900 font-medium text-sm">
              {crumb.label}
            </span>
          ) : (
            <button
              onClick={() => navigate(crumb.path)}
              className="text-blue-600 hover:text-blue-800 transition-colors text-sm font-medium"
            >
              {crumb.label}
            </button>
          )}
        </React.Fragment>
      ))}
    </nav>
  );
};

export default Breadcrumb;
